import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent } from '../ui/ResponsiveLayout';
import BrandSymbol from '../ui/BrandSymbol';
import Modal from '../ui/Modal';
import ReactMarkdown from 'react-markdown';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.endofanera.ai';

interface Task {
  id: string;
  name: string;
  completed: boolean;
  completedAt?: string;
  completedBy?: string;
}

interface Document {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
}

interface StepProgress {
  stepNumber: number;
  stepName: string;
  tasks: Task[];
  notes: string;
  documents: Document[];
  updatedAt?: string;
}

interface StepDefinition {
  number: number;
  name: string;
  description: string;
  icon: string;
  defaultTasks: { id: string; name: string }[];
  guidance: string;
}

const SETTLEMENT_STEPS: StepDefinition[] = [
  {
    number: 1,
    name: 'Gather Documents',
    description: 'Collect all essential legal and financial documents',
    icon: 'ðŸ“„',
    defaultTasks: [
      { id: 'death-cert', name: 'Obtain certified copies of death certificate (10-15 recommended)' },
      { id: 'will', name: 'Locate and secure the original will' },
      { id: 'trust', name: 'Locate trust documents (if applicable)' },
      { id: 'deeds', name: 'Gather property deeds and titles' },
      { id: 'insurance', name: 'Collect life insurance policies' },
      { id: 'bank-statements', name: 'Request recent bank and investment statements' },
      { id: 'tax-returns', name: 'Gather last 3 years of tax returns' },
      { id: 'birth-cert', name: 'Locate birth certificate and social security card' }
    ],
    guidance: `
### Why This Matters
Having organized documents will make every subsequent step easier and faster.

### Pro Tips
- Request at least 10-15 certified death certificates - many institutions require originals
- Keep all original documents in a secure location (safe deposit box or fireproof safe)
- Make copies of everything for your records
- Note where each document was found for future reference

### State-Specific Notes
Some states may require additional documentation for probate. Check with a local attorney.
`
  },
  {
    number: 2,
    name: 'Notify Relevant Parties',
    description: 'Inform all necessary individuals and institutions',
    icon: 'ðŸ“£',
    defaultTasks: [
      { id: 'family', name: 'Notify immediate family members' },
      { id: 'employer', name: 'Contact employer (HR and benefits department)' },
      { id: 'ssa', name: 'Notify Social Security Administration' },
      { id: 'banks', name: 'Contact all banks and financial institutions' },
      { id: 'insurance-co', name: 'File claims with insurance companies' },
      { id: 'creditors', name: 'Notify known creditors' },
      { id: 'utilities', name: 'Update or cancel utility accounts' },
      { id: 'subscriptions', name: 'Cancel subscriptions and memberships' },
      { id: 'post-office', name: 'Forward mail through USPS' }
    ],
    guidance: `
### Key Contacts
- **Social Security:** 1-800-772-1213 - Report death to stop benefits and request survivor benefits
- **Medicare:** Contact if the deceased was enrolled
- **Veterans Affairs:** 1-800-827-1000 - If the deceased was a veteran

### What to Have Ready
When contacting institutions, have these ready:
- Death certificate
- Your relationship to the deceased
- Account numbers (if known)
- Your contact information

### Important Timeline
Some notifications have deadlines:
- Social Security: As soon as possible
- Life insurance: Usually within 30-60 days
- Employer: Immediately for benefits purposes
`
  },
  {
    number: 3,
    name: 'Inventory Assets',
    description: 'Document all assets and their current values',
    icon: 'ðŸ“‹',
    defaultTasks: [
      { id: 'real-estate', name: 'List all real estate properties with current values' },
      { id: 'bank-accounts', name: 'Document all bank account balances' },
      { id: 'investments', name: 'List investment and brokerage accounts' },
      { id: 'retirement', name: 'Document retirement accounts (401k, IRA, pension)' },
      { id: 'vehicles', name: 'List vehicles with titles and values' },
      { id: 'personal-property', name: 'Inventory valuable personal property' },
      { id: 'business-interests', name: 'Document any business ownership or interests' },
      { id: 'digital-assets', name: 'Identify digital assets and cryptocurrencies' }
    ],
    guidance: `
### Valuation Tips
- Real estate: Get a formal appraisal or use recent comparable sales
- Vehicles: Use Kelley Blue Book or NADA guides
- Investments: Use date-of-death values
- Personal property: Consider professional appraisal for valuable items

### What to Document
For each asset, record:
- Description and location
- Account numbers (if applicable)
- Current value or date-of-death value
- How title is held (sole, joint, beneficiary designation)
- Any liens or encumbrances

### Don't Forget
- Safe deposit boxes
- Storage units
- Digital accounts with value
- Frequent flyer miles and credit card points
`
  },
  {
    number: 4,
    name: 'Manage Debts & Expenses',
    description: 'Pay legitimate debts and ongoing expenses',
    icon: 'ðŸ’°',
    defaultTasks: [
      { id: 'mortgage', name: 'Address mortgage or rent payments' },
      { id: 'credit-cards', name: 'Notify credit card companies and pay balances' },
      { id: 'medical-bills', name: 'Review and pay medical bills' },
      { id: 'funeral', name: 'Pay funeral and burial expenses' },
      { id: 'utilities-ongoing', name: 'Maintain essential utilities during estate settlement' },
      { id: 'estate-expenses', name: 'Track all estate administration expenses' },
      { id: 'verify-debts', name: 'Verify legitimacy of all claimed debts' }
    ],
    guidance: `
### Priority of Payment
Debts are typically paid in this order:
1. Funeral and burial expenses
2. Estate administration costs
3. Taxes
4. Secured debts (mortgages, car loans)
5. Unsecured debts (credit cards, personal loans)

### Important Notes
- You are NOT personally responsible for the deceased's debts unless you co-signed
- Don't pay debts before confirming they're legitimate
- Keep receipts for all payments made from estate funds
- Some debts may be dischargeable upon death

### Beware of Scams
- Never give personal information to unsolicited callers
- Verify all debt claims in writing
- Report suspicious collection attempts
`
  },
  {
    number: 5,
    name: 'File Taxes',
    description: 'Complete all required tax filings',
    icon: 'ðŸ“Š',
    defaultTasks: [
      { id: 'final-1040', name: 'File final personal income tax return (Form 1040)' },
      { id: 'ein', name: 'Obtain EIN for the estate (if needed)' },
      { id: 'estate-1041', name: 'File estate income tax return (Form 1041) if required' },
      { id: 'estate-706', name: 'File estate tax return (Form 706) if required' },
      { id: 'state-taxes', name: 'File any required state tax returns' },
      { id: 'property-taxes', name: 'Pay any outstanding property taxes' }
    ],
    guidance: `
### Key Deadlines
- **Final 1040:** Due April 15 of year following death (or extended date)
- **Form 1041:** Due April 15 for calendar year estates
- **Form 706:** Due 9 months after date of death (extension available)

### When Estate Tax Applies (2024)
Federal estate tax only applies if estate exceeds $13.61 million (2024)
- State estate taxes may have lower thresholds
- Consult with a tax professional for estates near thresholds

### Professional Help Recommended
Tax filings for estates can be complex. Consider hiring:
- CPA with estate experience
- Tax attorney for larger estates
- Enrolled agent familiar with estate taxes
`
  },
  {
    number: 6,
    name: 'Distribute Assets',
    description: 'Transfer assets to beneficiaries according to the will',
    icon: 'ðŸ¤',
    defaultTasks: [
      { id: 'verify-debts-paid', name: 'Confirm all debts and taxes are paid' },
      { id: 'final-accounting', name: 'Prepare final accounting of estate' },
      { id: 'beneficiary-approval', name: 'Get beneficiary approval of accounting (if required)' },
      { id: 'specific-bequests', name: 'Distribute specific bequests (named items)' },
      { id: 'cash-distributions', name: 'Calculate and distribute cash amounts' },
      { id: 'real-estate-transfer', name: 'Transfer real estate titles' },
      { id: 'investment-transfer', name: 'Transfer investment accounts' },
      { id: 'personal-property', name: 'Distribute personal property' }
    ],
    guidance: `
### Before Distributing
Make sure:
- All debts are paid or adequately reserved for
- All taxes are filed and paid
- Any potential claims period has passed
- You have proper authority to distribute

### Distribution Order
1. Specific bequests ("I leave my watch to...")
2. General bequests ("I leave $10,000 to...")
3. Residuary estate (everything that's left)

### Documentation
For each distribution:
- Get signed receipt from beneficiary
- Record date and description of transfer
- Keep copy of title transfer documents
- Document any disclaimers

### Special Considerations
- Minor beneficiaries: May need trust or custodial account
- Special needs beneficiaries: Consult attorney to protect benefits
- Out-of-state beneficiaries: Research tax implications
`
  },
  {
    number: 7,
    name: 'Close Estate',
    description: 'Complete final paperwork and close out the estate',
    icon: '✅',
    defaultTasks: [
      { id: 'final-accounting', name: 'Prepare and file final accounting with court (if probate)' },
      { id: 'release', name: 'Obtain releases from beneficiaries' },
      { id: 'close-accounts', name: 'Close remaining estate accounts' },
      { id: 'cancel-ein', name: 'Notify IRS of estate closure' },
      { id: 'court-filing', name: 'File petition to close estate with court (if probate)' },
      { id: 'document-archive', name: 'Archive all estate documents' },
      { id: 'final-notice', name: 'Send final notice to beneficiaries' }
    ],
    guidance: `
### Final Steps
- Ensure all distributions are complete
- Collect all signed receipts
- Close any remaining bank accounts
- Cancel estate EIN with IRS

### Document Retention
Keep these records for at least 7 years:
- All tax returns filed
- Final accounting
- Distribution receipts
- Correspondence with beneficiaries
- Receipts for expenses paid

### Court Closure (if probate)
- File final report with court
- Request discharge as executor
- Obtain court order closing estate

### Congratulations
Settling an estate is one of the most challenging responsibilities. 
You've honored your loved one by carefully managing their affairs.
`
  }
];

const SettlementFlow: React.FC = () => {
  const navigate = useNavigate();
  const { estateId } = useParams<{ estateId: string }>();
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get('step');
  
  const [currentStep, setCurrentStep] = useState(stepParam ? parseInt(stepParam) : 1);
  const [progress, setProgress] = useState<StepProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [estateOwnerName, setEstateOwnerName] = useState('');
  
  // Document upload
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const currentStepDef = SETTLEMENT_STEPS.find(s => s.number === currentStep);
  const currentProgress = progress.find(p => p.stepNumber === currentStep);

  const fetchProgress = useCallback(async () => {
    if (!estateId) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('account-token');
      
      // Fetch estate info
      const estateResponse = await fetch(`${API_BASE_URL}/api/settlement/${estateId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (estateResponse.ok) {
        const estateData = await estateResponse.json();
        setEstateOwnerName(estateData.estateOwner?.name || '');
        
        if (estateData.accessLevel !== 'full') {
          navigate(`/executor/${estateId}`);
          return;
        }
      }

      // Fetch progress
      const progressResponse = await fetch(`${API_BASE_URL}/api/settlement/${estateId}/progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (progressResponse.ok) {
        const data = await progressResponse.json();
        
        // Merge with default tasks
        const mergedProgress = SETTLEMENT_STEPS.map(step => {
          const existing = data.progress?.find((p: StepProgress) => p.stepNumber === step.number);
          
          const tasks = step.defaultTasks.map(dt => {
            const existingTask = existing?.tasks?.find((t: Task) => t.id === dt.id);
            return existingTask || { id: dt.id, name: dt.name, completed: false };
          });
          
          return {
            stepNumber: step.number,
            stepName: step.name,
            tasks,
            notes: existing?.notes || '',
            documents: existing?.documents || [],
            updatedAt: existing?.updatedAt
          };
        });
        
        setProgress(mergedProgress);
      }
    } catch (err: unknown) {
      console.error('Error fetching progress:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [estateId, navigate]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const handleTaskToggle = async (taskId: string) => {
    if (!currentProgress || !estateId) return;
    
    const updatedTasks = currentProgress.tasks.map(task => {
      if (task.id === taskId) {
        return {
          ...task,
          completed: !task.completed,
          completedAt: !task.completed ? new Date().toISOString() : undefined
        };
      }
      return task;
    });

    // Optimistic update
    setProgress(prev => prev.map(p => 
      p.stepNumber === currentStep ? { ...p, tasks: updatedTasks } : p
    ));

    // Save to backend
    setSaving(true);
    try {
      const token = localStorage.getItem('account-token');
      await fetch(`${API_BASE_URL}/api/settlement/${estateId}/step/${currentStep}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tasks: updatedTasks,
          notes: currentProgress.notes
        })
      });
    } catch (err) {
      console.error('Error saving progress:', err);
      // Revert on error
      fetchProgress();
    } finally {
      setSaving(false);
    }
  };

  const handleNotesChange = async (notes: string) => {
    if (!currentProgress || !estateId) return;
    
    // Optimistic update
    setProgress(prev => prev.map(p => 
      p.stepNumber === currentStep ? { ...p, notes } : p
    ));

    // Debounced save
    setSaving(true);
    try {
      const token = localStorage.getItem('account-token');
      await fetch(`${API_BASE_URL}/api/settlement/${estateId}/step/${currentStep}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tasks: currentProgress.tasks,
          notes
        })
      });
    } catch (err) {
      console.error('Error saving notes:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async () => {
    if (!uploadFile || !estateId) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('account-token');
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target?.result?.toString().split(',')[1];
        
        const response = await fetch(`${API_BASE_URL}/api/settlement/${estateId}/step/${currentStep}/document`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            fileName: uploadFile.name,
            fileType: uploadFile.type,
            fileSize: uploadFile.size,
            fileData: base64Data
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to upload document');
        }
        
        setShowUploadModal(false);
        setUploadFile(null);
        fetchProgress();
      };
      
      reader.readAsDataURL(uploadFile);
    } catch (err: unknown) {
      console.error('Error uploading document:', err);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const getStepCompletionPercentage = (step: StepProgress) => {
    if (!step.tasks.length) return 0;
    const completed = step.tasks.filter(t => t.completed).length;
    return Math.round((completed / step.tasks.length) * 100);
  };

  const getOverallProgress = () => {
    const totalTasks = progress.reduce((acc, p) => acc + p.tasks.length, 0);
    const completedTasks = progress.reduce((acc, p) => acc + p.tasks.filter(t => t.completed).length, 0);
    return totalTasks ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  if (loading) {
    return (
      <ResponsiveContainer background="stone">
        <ResponsiveContent>
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
            <p className="mt-4 text-stone-600">Loading settlement progress...</p>
          </div>
        </ResponsiveContent>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        {/* Header */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-full max-w-5xl mb-4">
            <button
              onClick={() => navigate(`/executor/${estateId}`)}
              className="text-cyan-700 hover:text-cyan-800 text-sm font-medium flex items-center gap-2"
            >
              ← Back to Estate Dashboard
            </button>
          </div>
          <BrandSymbol size="3xl" variant="dark" showText={true} className="mb-4" />
          <h1 className="font-display text-2xl font-semibold text-stone-800">
            Settlement Process
          </h1>
          <p className="text-stone-600">{estateOwnerName}'s Estate</p>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Overall Progress */}
          <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-stone-700">Overall Progress</span>
              <span className="text-sm font-bold text-cyan-700">{getOverallProgress()}%</span>
            </div>
            <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-cyan-600 to-cyan-400 rounded-full transition-all duration-500"
                style={{ width: `${getOverallProgress()}%` }}
              />
            </div>
          </div>

          {/* Step Navigation */}
          <div className="grid grid-cols-7 gap-2 mb-6">
            {SETTLEMENT_STEPS.map((step) => {
              const stepProgress = progress.find(p => p.stepNumber === step.number);
              const pct = stepProgress ? getStepCompletionPercentage(stepProgress) : 0;
              const isActive = step.number === currentStep;
              
              return (
                <button
                  key={step.number}
                  onClick={() => setCurrentStep(step.number)}
                  className={`relative p-3 rounded-lg text-center transition-all ${
                    isActive 
                      ? 'bg-cyan-700 text-white shadow-lg' 
                      : 'bg-white text-stone-600 hover:bg-stone-50 border border-stone-200'
                  }`}
                >
                  <div className="text-2xl mb-1">{step.icon}</div>
                  <div className={`text-xs font-medium ${isActive ? 'text-white' : 'text-stone-500'}`}>
                    Step {step.number}
                  </div>
                  {pct === 100 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Current Step Content */}
          {currentStepDef && currentProgress && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {/* Step Header */}
                <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-4xl">{currentStepDef.icon}</div>
                    <div>
                      <h2 className="text-xl font-semibold text-stone-800">
                        Step {currentStepDef.number}: {currentStepDef.name}
                      </h2>
                      <p className="text-stone-600">{currentStepDef.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-cyan-600 rounded-full transition-all duration-300"
                        style={{ width: `${getStepCompletionPercentage(currentProgress)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-stone-600">
                      {currentProgress.tasks.filter(t => t.completed).length}/{currentProgress.tasks.length} tasks
                    </span>
                  </div>
                </div>

                {/* Tasks */}
                <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
                  <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Tasks
                    {saving && <span className="text-xs text-stone-400 ml-2">Saving...</span>}
                  </h3>
                  
                  <div className="space-y-3">
                    {currentProgress.tasks.map((task) => (
                      <label
                        key={task.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          task.completed 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-stone-50 border-stone-200 hover:border-cyan-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={task.completed}
                          onChange={() => handleTaskToggle(task.id)}
                          className="w-5 h-5 mt-0.5 text-cyan-700 border-stone-300 rounded focus:ring-cyan-500"
                        />
                        <span className={`flex-1 ${task.completed ? 'text-green-800 line-through' : 'text-stone-700'}`}>
                          {task.name}
                        </span>
                        {task.completed && task.completedAt && (
                          <span className="text-xs text-green-600">
                            {new Date(task.completedAt).toLocaleDateString()}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
                  <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Notes
                  </h3>
                  <textarea
                    value={currentProgress.notes}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    placeholder="Add notes about this step (contact names, important dates, reminders...)"
                    className="w-full h-32 px-4 py-3 border border-stone-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                {/* Documents */}
                <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
                      <svg className="w-5 h-5 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      Documents
                    </h3>
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="text-sm text-cyan-700 hover:text-cyan-800 font-medium"
                    >
                      + Upload Document
                    </button>
                  </div>
                  
                  {currentProgress.documents.length === 0 ? (
                    <div className="text-center py-8 text-stone-500">
                      <svg className="w-12 h-12 mx-auto text-stone-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <p>No documents uploaded for this step</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {currentProgress.documents.map((doc) => (
                        <div key={doc.id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                          <svg className="w-6 h-6 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-stone-800 truncate">{doc.name}</p>
                            <p className="text-xs text-stone-500">
                              Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar - Guidance */}
              <div className="lg:col-span-1">
                <div className="bg-cyan-50 rounded-xl border border-cyan-200 p-6 sticky top-4">
                  <h3 className="text-lg font-semibold text-cyan-900 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Guidance
                  </h3>
                  <div className="text-sm text-cyan-800 space-y-4 [&_h3]:text-cyan-900 [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:ml-4 [&_li]:mb-1 [&_strong]:font-semibold">
                    <ReactMarkdown>{currentStepDef.guidance}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
              disabled={currentStep === 1}
              className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 ${
                currentStep === 1
                  ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                  : 'bg-white text-stone-700 border border-stone-300 hover:bg-stone-50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous Step
            </button>
            
            <button
              onClick={() => setCurrentStep(prev => Math.min(7, prev + 1))}
              disabled={currentStep === 7}
              className={`px-6 py-2 rounded-lg font-medium flex items-center gap-2 ${
                currentStep === 7
                  ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                  : 'bg-cyan-700 text-white hover:bg-cyan-800'
              }`}
            >
              Next Step
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Upload Modal */}
        <Modal
          open={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setUploadFile(null);
          }}
          title="Upload Document"
        >
          <div className="space-y-4">
            <p className="text-sm text-stone-600">
              Upload a document related to {currentStepDef?.name}. Accepted formats: PDF, JPG, PNG, DOC, DOCX (max 10MB).
            </p>
            
            <div className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="hidden"
                id="doc-upload"
              />
              <label htmlFor="doc-upload" className="cursor-pointer">
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-stone-800 font-medium">{uploadFile.name}</span>
                  </div>
                ) : (
                  <div>
                    <svg className="w-10 h-10 text-stone-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-cyan-700 font-medium">Click to select file</span>
                  </div>
                )}
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                }}
                className="px-4 py-2 text-stone-600"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handleDocumentUpload}
                disabled={!uploadFile || uploading}
                className={`px-6 py-2 rounded-lg text-white font-medium ${
                  !uploadFile || uploading
                    ? 'bg-stone-400 cursor-not-allowed'
                    : 'bg-cyan-700 hover:bg-cyan-800'
                }`}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </Modal>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default SettlementFlow;

